import React, { useContext, useMemo } from 'react';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { Fab } from '@mui/material';
import { isEmpty } from 'lodash/lang';

import { EDIT_INVOICE } from '../../../../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../../../../services/auth';
import { PARENT } from '../../../InvoiceTable/TableConstants';
import { InvoiceContext } from '../../../NewInvoiceContext';
import { ACTION_BUTTON_COLOR } from '../../CreateInvoiceConstants';
import { getFileForSandingEmail, openSendmailModal } from '../../CreateInvoiceServices';

export const CreateInvoiceActionPanel = ({
  invoiceCreated,
  fileName,
  PDFFile,
  selectedBillTo,
  selectedPayee,
  onSave,
  savingType,
  handlePrint,
  downloadFile,
  emailParams,
  generationInProgress,
  expenseAttachmentIds,
  setInvoiceNumber,
  currentInvoiceType,
  pdfWasEdited,
  invoiceWasReconciled
}) => {
  const { createInvoiceItems, loadInvoice, onEditPDF, editPDFMode, isInvalidInvoiceDate } = useContext(InvoiceContext);
  const canEditInvoice = userHasAccessTo(EDIT_INVOICE);

  const editButtonAvailable = useMemo(() => {
    return (
      invoiceCreated &&
      !editPDFMode &&
      currentInvoiceType === PARENT &&
      createInvoiceItems.originalLedgerEvents?.filter(event => event.invoiceType !== PARENT)?.length === 0 &&
      canEditInvoice &&
      !invoiceWasReconciled
    );
  }, [
    invoiceCreated,
    editPDFMode,
    currentInvoiceType,
    createInvoiceItems?.originalLedgerEvents,
    canEditInvoice,
    invoiceWasReconciled
  ]);

  const buttonsDisabled = useMemo(() => {
    return !!savingType || generationInProgress || pdfWasEdited || isEmpty(selectedPayee) || isEmpty(selectedBillTo);
  }, [generationInProgress, pdfWasEdited, savingType, selectedBillTo, selectedPayee]);

  return (
    <div className="action-panel">
      <Fab
        data-testid="send-invoice-button"
        onClick={() =>
          invoiceCreated
            ? openSendmailModal({
                ...emailParams,
                initialFiles: getFileForSandingEmail(createInvoiceItems?.fileName ?? `${fileName}.pdf`, PDFFile),
                loadInvoice,
                expenseAttachmentIds,
                createInvoiceItems,
                setInvoiceNumber
              })
            : onSave('SEND')
        }
        disabled={buttonsDisabled}
      >
        <SendIcon sx={{ color: ACTION_BUTTON_COLOR }} />
      </Fab>
      <Fab
        data-testid="download-invoice-button"
        onClick={() => (invoiceCreated ? downloadFile(PDFFile) : onSave('DOWNLOAD'))}
        disabled={buttonsDisabled}
      >
        <DownloadIcon sx={{ color: ACTION_BUTTON_COLOR }} />
      </Fab>
      <Fab
        data-testid="print-invoice-button"
        onClick={() => (invoiceCreated ? handlePrint(PDFFile) : onSave('PRINT'))}
        disabled={buttonsDisabled}
      >
        <PrintIcon sx={{ color: ACTION_BUTTON_COLOR }} />
      </Fab>
      {(!invoiceCreated || editPDFMode) && (
        <Fab
          data-testid="save-invoice-button"
          onClick={() => (!invoiceCreated || editPDFMode) && onSave()}
          disabled={
            (invoiceCreated && !editPDFMode) ||
            !!savingType ||
            isEmpty(selectedPayee) ||
            isEmpty(selectedBillTo) ||
            generationInProgress ||
            (editPDFMode && !canEditInvoice) ||
            (editPDFMode && !pdfWasEdited) ||
            isInvalidInvoiceDate
          }
        >
          <SaveIcon sx={{ color: ACTION_BUTTON_COLOR }} />
        </Fab>
      )}
      {editButtonAvailable && (
        <Fab data-testid="edit-invoice-button" onClick={onEditPDF}>
          <BorderColorIcon sx={{ color: ACTION_BUTTON_COLOR }} />
        </Fab>
      )}
    </div>
  );
};
